/**
 * Loading, empty, error, permission-denied and outage states.
 *
 * These are acceptance criteria in the handoff, not deferred polish, and they are
 * written as distinct components because they are distinct situations that a
 * single spinner-or-nothing conflates:
 *
 * - **empty** — the query worked and there is nothing. Says what would put
 *   something here.
 * - **permission-denied** — the query worked and the role may not see it. Says
 *   which role, so a persona demo is comprehensible rather than mysterious.
 * - **outage** — the API or the database is unreachable. Distinguished from an
 *   error because the remedy is "start the server", and that is worth saying in
 *   development.
 * - **error** — anything else, with the server's own message, because a generic
 *   "something went wrong" throws away the only useful information.
 */
import { useEffect, useState, type ReactNode } from "react";
import { ApiClientError } from "../lib/api";
import { Blueprint } from "./primitives";

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div
      className="text-muted"
      style={{ padding: "var(--space-6)", fontSize: "var(--tb-text-sm)" }}
      role="status"
      aria-live="polite"
    >
      {label}
    </div>
  );
}

export function EmptyState({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) {
  return (
    <Blueprint className="panel">
      <h5 style={{ margin: 0 }}>{title}</h5>
      {children ? (
        <p className="text-muted" style={{ fontSize: "var(--tb-text-sm)", margin: "var(--space-2) 0 0", maxWidth: "70ch" }}>
          {children}
        </p>
      ) : null}
    </Blueprint>
  );
}

export function PermissionDenied({ role, what }: { role: string; what: string }) {
  return (
    <Blueprint className="panel">
      <h5 style={{ margin: 0 }}>Not available to your role</h5>
      <p className="text-muted" style={{ fontSize: "var(--tb-text-sm)", margin: "var(--space-2) 0 0" }}>
        Your role ({role}) can&rsquo;t {what}.
      </p>
    </Blueprint>
  );
}

/**
 * One error renderer that decides which situation it is looking at.
 *
 * Screens call this rather than branching themselves, so a 403 never renders as a
 * red failure and an outage never renders as a bug in the page.
 */
export function QueryError({ error, what }: { error: Error; what: string }) {
  if (error instanceof ApiClientError) {
    if (error.code === "network") {
      return (
        <Blueprint className="panel">
          <h5 style={{ margin: 0 }}>Can&rsquo;t connect to TruckBuddy</h5>
          <p className="text-muted" style={{ fontSize: "var(--tb-text-sm)", margin: "var(--space-2) 0 0" }}>
            Check your connection and reload.
          </p>
        </Blueprint>
      );
    }
    if (error.status === 401) {
      return (
        <Blueprint className="panel">
          <h5 style={{ margin: 0 }}>Not signed in</h5>
          <p className="text-muted" style={{ fontSize: "var(--tb-text-sm)", margin: "var(--space-2) 0 0" }}>
            {error.message}
          </p>
        </Blueprint>
      );
    }
    if (error.status === 403) {
      return (
        <Blueprint className="panel">
          <h5 style={{ margin: 0 }}>Not permitted</h5>
          <p className="text-muted" style={{ fontSize: "var(--tb-text-sm)", margin: "var(--space-2) 0 0" }}>
            {error.message}
          </p>
        </Blueprint>
      );
    }
    if (error.status === 503) {
      return (
        <Blueprint className="panel">
          <h5 style={{ margin: 0 }}>A source is unavailable</h5>
          <p className="text-muted" style={{ fontSize: "var(--tb-text-sm)", margin: "var(--space-2) 0 0" }}>
            {error.message} Nothing was saved. Try again shortly.
          </p>
        </Blueprint>
      );
    }
  }
  return (
    <Blueprint className="panel">
      <h5 style={{ margin: 0 }}>Could not load {what}</h5>
      <p className="text-muted" style={{ fontSize: "var(--tb-text-sm)", margin: "var(--space-2) 0 0" }}>
        {error.message}
      </p>
    </Blueprint>
  );
}

/**
 * The standard read wrapper: loading, then error, then the data.
 *
 * `data` is narrowed to non-undefined inside `children`, which is what stops the
 * `data!` assertions that otherwise spread through every screen.
 */
export function AsyncPanel<T>({
  query,
  what,
  children,
  emptyWhen,
  empty,
}: {
  query: { data: T | undefined; isLoading: boolean; error: Error | null };
  what: string;
  children: (data: T) => ReactNode;
  emptyWhen?: (data: T) => boolean;
  empty?: ReactNode;
}) {
  if (query.isLoading) return <Loading label={`Loading ${what}…`} />;
  if (query.error) return <QueryError error={query.error} what={what} />;
  if (query.data === undefined) return <Loading label={`Loading ${what}…`} />;
  if (emptyWhen?.(query.data) && empty) return <>{empty}</>;
  return <>{children(query.data)}</>;
}

/* ── holding a region until its reads settle — TRBUD-297 ───────────────
 *
 * Measured on 24 September 2026, Lighthouse desktop: Reports CLS 0.987,
 * Configuration 1.067, Settings > Permissions 0.773 (0.655 on a phone), Repair
 * overview 0.195. One mechanism on all four, read off the layout-shift entries
 * (the shifted nodes' previous and current rects), not guessed:
 *
 * A screen paints every panel at once, most of them as a one-line "Loading…",
 * and a read near the TOP then resolves into something several hundred pixels
 * tall -- the KPI cards, the type editor, the switch board. Everything already
 * painted below it is pushed down and usually out of the viewport. On Reports
 * the category and purchase-order panels went from y=374 to below the fold in
 * one frame (0.615 of the 0.72); on Permissions the read-only capability
 * matrix, which needs no server data and so painted first, was pushed out by
 * the switch board above it (0.759). The order is the defect: content that is
 * ready early sits BELOW content that is ready late.
 *
 * Reserving the late content's height was rejected, as the 10 September report
 * already argued: every one of these heights depends on the data (the number of
 * switches, the length of a KPI caveat, how many work-order types exist) and on
 * the viewport, so a guessed min-height trades a downward shift for an upward
 * one. Reordering the panels would change the page.
 *
 * So the region below the first data-dependent section waits, and paints once:
 *
 * - **Settled means data OR error.** A failing read releases the hold exactly as
 *   a successful one does, so every panel still reports its own failure. That
 *   independence is a stated design on each of these screens ("a failing
 *   `/reports` must not take out a table that would still have loaded"), and a
 *   hold that waited for SUCCESS would break it.
 * - **`isLoading`, not `isPending`.** `isLoading` is a first load actually in
 *   flight. A read switched off with `enabled: false` is pending for ever and
 *   never loading, so keying on `isPending` would hold the region for ever --
 *   the softlock CLAUDE.md's cold-start section describes, built from a gate.
 * - **Latched.** Once released it stays released for the life of the
 *   component. Changing Reports' timeframe starts a new first load under a new
 *   cache key; hiding the whole page again for that would be a regression, and
 *   a shift that follows the reader's own click is not counted as layout shift
 *   anyway (`hadRecentInput`).
 * - **A ceiling.** After `SETTLE_CEILING_MS` the region renders regardless, with
 *   each panel's own loading state -- which is exactly today's behaviour. So the
 *   worst a hung or slow read can do is cost the page its stability, never
 *   withhold the panels that did load. Without the ceiling, one read that never
 *   answered would hold every panel beneath it on a spinner.
 *
 * The children render as a FRAGMENT, never inside a wrapper element: the section
 * rhythm is `.screen-body > * + *` in industry.css, so a wrapping div would
 * silently take every held panel out of it and change the loaded page.
 */

/**
 * How long a held region waits for its reads before rendering anyway.
 *
 * Three seconds is past a healthy read on a slow phone connection and about when
 * a read that is failing has spent its two retries (`main.tsx` retries a 5xx
 * twice, one second then two), so a failure usually releases the hold on its own
 * just before the ceiling would.
 */
export const SETTLE_CEILING_MS = 3_000;

/** The one field a hold reads — every `UseQueryResult` has it. */
export interface SettlingRead {
  isLoading: boolean;
}

/**
 * True once none of `reads` is on its first load, or once the ceiling has
 * passed. Latched: it never goes back to false.
 */
export function useFirstLoadsSettled(
  reads: ReadonlyArray<SettlingRead>,
  ceilingMs: number = SETTLE_CEILING_MS,
): boolean {
  const loading = reads.some((read) => read.isLoading);
  const [released, setReleased] = useState(!loading);
  useEffect(() => {
    if (released) return;
    if (!loading) {
      setReleased(true);
      return;
    }
    const timer = setTimeout(() => setReleased(true), ceilingMs);
    return () => clearTimeout(timer);
  }, [loading, released, ceilingMs]);
  // `|| !loading` so the render in which the last read settles paints the
  // region itself, rather than one more frame of the loading line.
  return released || !loading;
}

/**
 * Render `children` only once `reads` have settled; until then, ONE loading line.
 *
 * Put it around everything from a screen's first data-dependent section to the
 * end of its body, and pass the reads of every panel inside it. A panel whose
 * read is missing from `reads` still paints its own loading line when the hold
 * opens and can still grow afterwards -- so when a panel gains a read, it goes
 * in the list too.
 */
export function HoldUntilSettled({
  reads,
  label,
  children,
  ceilingMs,
}: {
  reads: ReadonlyArray<SettlingRead>;
  /** What the single loading line says while the region is held. */
  label: string;
  children: ReactNode;
  ceilingMs?: number;
}) {
  const settled = useFirstLoadsSettled(reads, ceilingMs);
  if (!settled) return <Loading label={label} />;
  return <>{children}</>;
}
